import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import KnowledgeBaseSyncStatusPanel from './KnowledgeBaseSyncStatusPanel';
import ErrorBoundary from './ErrorBoundary';

/**
 * KnowledgeBaseSyncStatusModal Component
 * 
 * Modal dialog for displaying detailed Knowledge Base sync status for a parrot.
 * Provides a focused view of sync operations with retry controls and detailed
 * status information.
 * 
 * Requirements: 3.1, 3.2, 3.4, 3.6
 */
const KnowledgeBaseSyncStatusModal = ({
    isOpen,
    onClose,
    parrotId,
    parrotName,
    className=''
}) => {
    const { theme } = useTheme();

    if (!isOpen) return null;

    return (
        <ErrorBoundary>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className={`rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                } ${className}`}>
                    {/* Header */}
                    <div className={`px-6 py-4 border-b ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className={`text-xl font-semibold ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    Knowledge Base Sync Status
                                </h2>
                                <p className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                } mt-1`}>
                                    Detailed sync status for {parrotName}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className={`p-2 rounded-lg transition-colors ${
                                    theme === 'dark' 
                                        ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                }`}
                                aria-label="Close modal"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
                        <div className="p-6">
                            <KnowledgeBaseSyncStatusPanel
                                parrotId={parrotId}
                                parrotName={parrotName}
                                showHeader={false}
                                expandedByDefault={true}
                                autoRefresh={true}
                                refreshInterval={15000} // More frequent updates in modal
                                onStatusChange={(status) => {
                                    // Could emit events for parent components
                                    console.log('Status updated:', status);
                                }}
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className={`px-6 py-4 border-t ${
                        theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
                    }`}>
                        <div className="flex justify-end">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors dark:text-white"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default KnowledgeBaseSyncStatusModal;
